import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  FindJobsByWhereForDetailViewArgs,
  FindJobsByWhereForDetailViewResult,
  FindJobsByWhereIncludeAnyArgs,
  FindJobsByWhereIncludeAnyItem,
} from '@app/auto-interfaces';
import { Axios } from '@app/web-components';

const JOB_ALL_QUERY_KEY = 'JOB_ALL_QUERY_KEY';

export const jobKeys = {
  all: [JOB_ALL_QUERY_KEY] as const,
  lists: () => [...jobKeys.all, 'list'] as const,
  list: (filters: object) => [...jobKeys.lists(), filters] as const,
};

export const useFindJob = (args: FindJobsByWhereForDetailViewArgs) => {
  const axios = Axios.useAxiosApi();

  return useQuery<FindJobsByWhereForDetailViewResult>({
    queryKey: jobKeys.list(args),
    queryFn: async () => {
      const { data } = await axios.post('/jobs/where-detail-view', args);
      return data;
    },
    refetchInterval: 60000,
  });
};

export const useFindJobById = <T extends FindJobsByWhereIncludeAnyArgs & { enabled?: boolean }>({
  enabled,
  ...args
}: T) => {
  const axios = Axios.useAxiosApi();

  return useQuery<FindJobsByWhereIncludeAnyItem<T>>({
    queryKey: jobKeys.list(args),
    queryFn: async () => {
      const { data } = await axios.post('/jobs/where-include-any', args);
      return data.data.length > 0 ? data.data[0] : undefined;
    },
    enabled,
  });
};

export const useRetryJob = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: number) => {
      return axios.post('/jobs/retry', { id: jobId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [jobKeys.lists] });
    },
  });
};
